import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import NewsManagement from './admin/NewsManagement';
import LeadershipManagement from './admin/LeadershipManagement';
import ValuesManagement from './admin/ValuesManagement';

// Test mode components that bypass authentication
const TestNewsManagement = () => {
  // Mock authenticated state for testing
  const mockUser = { name: 'Test User', email: 'test@vincotek.com' };
  
  return (
    <div className="p-6">
      <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 rounded">
        <p className="text-sm text-yellow-800">
          <strong>TEST MODE:</strong> Authentication bypassed for testing purposes
        </p>
      </div>
      <NewsManagement />
    </div>
  );
};

const TestLeadershipManagement = () => {
  return (
    <div className="p-6">
      <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 rounded">
        <p className="text-sm text-yellow-800">
          <strong>TEST MODE:</strong> Authentication bypassed for testing purposes
        </p>
      </div>
      <LeadershipManagement />
    </div>
  );
};

const TestValuesManagement = () => {
  return (
    <div className="p-6">
      <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 rounded">
        <p className="text-sm text-yellow-800">
          <strong>TEST MODE:</strong> Authentication bypassed for testing purposes
        </p>
      </div>
      <ValuesManagement />
    </div>
  );
};

const AdminTest = () => {
  const [currentView, setCurrentView] = useState('login');
  const [testMode, setTestMode] = useState(false);

  const renderView = () => {
    if (testMode) {
      switch (currentView) {
        case 'login':
          return <AdminLogin />;
        case 'dashboard':
          return (
            <div className="p-6">
              <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>TEST MODE:</strong> Showing dashboard stats without authentication
                </p>
              </div>
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-vincotek-dark">Dashboard Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 group">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">News Articles</p>
                          <p className="text-3xl font-bold text-vincotek-dark group-hover:text-blue-600 transition-colors">2</p>
                          <p className="text-xs text-gray-500 mt-1">Click to manage</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                          📰
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 group">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Leadership Members</p>
                          <p className="text-3xl font-bold text-vincotek-dark group-hover:text-green-600 transition-colors">2</p>
                          <p className="text-xs text-gray-500 mt-1">Click to manage</p>
                        </div>
                        <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center group-hover:bg-green-100 transition-colors">
                          👥
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 group">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Core Values</p>
                          <p className="text-3xl font-bold text-vincotek-dark group-hover:text-yellow-600 transition-colors">2</p>
                          <p className="text-xs text-gray-500 mt-1">Click to manage</p>
                        </div>
                        <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center group-hover:bg-yellow-100 transition-colors">
                          ❤️
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          );
        case 'news':
          return <TestNewsManagement />;
        case 'leadership':
          return <TestLeadershipManagement />;
        case 'values':
          return <TestValuesManagement />;
        default:
          return <AdminLogin />;
      }
    } else {
      switch (currentView) {
        case 'login':
          return <AdminLogin />;
        case 'dashboard':
          return <AdminDashboard />;
        case 'news':
          return <NewsManagement />;
        case 'leadership':
          return <LeadershipManagement />;
        case 'values':
          return <ValuesManagement />;
        default:
          return <AdminLogin />;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar for Testing */}
      <div className="bg-white shadow-sm border-b p-4">
        <div className="container mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-vincotek-dark">Admin Test Interface</h1>
          
          <div className="flex items-center space-x-4">
            <Button 
              size="sm" 
              variant={testMode ? 'default' : 'outline'}
              onClick={() => setTestMode(!testMode)}
              className={testMode ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {testMode ? '✅ Test Mode ON' : 'Enable Test Mode'}
            </Button>
            
            <div className="flex space-x-2">
            <Button 
              size="sm" 
              variant={currentView === 'login' ? 'default' : 'outline'}
              onClick={() => setCurrentView('login')}
            >
              Login
            </Button>
            <Button 
              size="sm" 
              variant={currentView === 'dashboard' ? 'default' : 'outline'}
              onClick={() => setCurrentView('dashboard')}
            >
              Dashboard
            </Button>
            <Button 
              size="sm" 
              variant={currentView === 'news' ? 'default' : 'outline'}
              onClick={() => setCurrentView('news')}
            >
              News
            </Button>
            <Button 
              size="sm" 
              variant={currentView === 'leadership' ? 'default' : 'outline'}
              onClick={() => setCurrentView('leadership')}
            >
              Leadership
            </Button>
            <Button 
              size="sm" 
              variant={currentView === 'values' ? 'default' : 'outline'}
              onClick={() => setCurrentView('values')}
            >
              Values
            </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Current View */}
      <div>
        {renderView()}
      </div>
    </div>
  );
};

export default AdminTest;